import React, { useState, useEffect, useRef } from 'react';
import { Trip } from '../types';

interface AddDayModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (dayData: {
    dayDate: string;
    title: string;
    summary: string;
    lodgingInfo: string;
  }) => void;
  onSaveAndShowRecommendations: (dayData: {
    dayDate: string;
    title: string;
    summary: string;
    lodgingInfo: string;
  }) => void;
  onAIGenerateWithActivities: (dayData: {
    dayDate: string;
    title: string;
    summary: string;
    lodgingInfo: string;
  }) => void;
  onAIGenerateSummary: (summary: string) => Promise<{ summary: string; lodgingInfo: string }>;
  onAIGenerateNewDay?: (prompt: string, suggestedDate?: string) => Promise<{ dayDate: string; title: string; summary: string; lodgingInfo: string }>;
  trip?: Trip | null;
  isLoading?: boolean;
  isSaving?: boolean;
  isGeneratingSummary?: boolean;
}

const AddDayModal: React.FC<AddDayModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onSaveAndShowRecommendations,
  onAIGenerateWithActivities,
  onAIGenerateNewDay,
  trip,
  isLoading = false,
  isSaving = false
}) => {
  const [dayDate, setDayDate] = useState('');
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [lodgingInfo, setLodgingInfo] = useState('');
  const [isAIMode, setIsAIMode] = useState(true); // AI mode by default
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const scrollPositionRef = useRef<number>(0);

  // Calculate next date when modal opens
  useEffect(() => {
    if (isOpen && trip?.days && trip.days.length > 0) {
      // Get the last day's date and add 1 day
      const lastDay = trip.days[trip.days.length - 1];
      const lastDate = new Date(lastDay.dayDate);
      lastDate.setDate(lastDate.getDate() + 1);
      setDayDate(lastDate.toISOString().split('T')[0]);
    } else if (isOpen) {
      // If no days exist, use tomorrow's date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setDayDate(tomorrow.toISOString().split('T')[0]);
    }
  }, [isOpen, trip]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setSummary('');
      setLodgingInfo('');
      setIsAIMode(true);
      setAiPrompt('');
      setIsGeneratingAI(false);
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  // Close dropdown when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isDropdownOpen) {
        const dropdown = document.querySelector('[data-dropdown="day-save-dropdown"]');
        const button = document.querySelector('[data-button="day-save-button"]');
        
        if (dropdown && !dropdown.contains(event.target as Node) && 
            button && !button.contains(event.target as Node)) {
          setIsDropdownOpen(false);
        }
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      scrollPositionRef.current = window.scrollY;
      // Prevent body scroll immediately
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Cleanup: restore scroll position
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollPositionRef.current);
      };
    } else {
      // Restore scroll position when closing
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollPositionRef.current);
    }
  }, [isOpen]);

  const handleAIApply = async () => {
    if (!aiPrompt.trim()) {
      alert('Please enter a description of the day you want to create');
      return;
    }
    
    if (!onAIGenerateNewDay) {
      alert('AI generation is not available. Please use Manual Mode.');
      return;
    }
    
    setIsGeneratingAI(true);
    try {
      const result = await onAIGenerateNewDay(aiPrompt, dayDate || undefined);
      
      // Populate the form with the generated data
      setDayDate(result.dayDate);
      setTitle(result.title);
      setSummary(result.summary);
      setLodgingInfo(result.lodgingInfo || '');
      
      // Clear the AI prompt and switch to manual mode
      setAiPrompt('');
      setIsAIMode(false);
    } catch (error) {
      console.error('Error generating with AI:', error);
      alert(`Failed to generate day: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleDropdownAction = (action: 'save' | 'saveAndRecommendations' | 'saveWithActivities') => {
    setIsDropdownOpen(false);
    
    if (!dayDate || !title || !summary) {
      alert('Please fill in date, day title, and day summary');
      return;
    }

    const dayData = {
      dayDate,
      title,
      summary,
      lodgingInfo
    };

    switch (action) {
      case 'save':
        onSave(dayData);
        break;
      case 'saveAndRecommendations':
        onSaveAndShowRecommendations(dayData);
        break;
      case 'saveWithActivities':
        onAIGenerateWithActivities(dayData);
        break;
    }
  };

  const handleClose = () => {
    // Reset form
    setTitle('');
    setSummary('');
    setLodgingInfo('');
    setIsAIMode(true);
    setAiPrompt('');
    setIsGeneratingAI(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay"
      onWheel={(e) => {
        // Prevent scroll on overlay, but allow it within modal content
        const target = e.target as HTMLElement;
        const modalContent = target.closest('.modal-content');
        // If the event is on the overlay itself (not inside modal content), prevent it
        if (e.currentTarget === e.target || !modalContent) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      style={{ overflow: 'hidden' }}
    >
      <div className="modal-content max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header - styled like Edit Day modal */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-[#E3E8E5] flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#0B1412]">
            Add New Day
          </h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F2FBF9] transition-colors"
            disabled={isSaving || isLoading || isGeneratingAI}
            aria-label="Close"
          >
            <svg className="w-5 h-5 text-[#6F7974]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            {/* Mode Toggle */}
            <div className="flex items-center justify-between p-4 bg-[#F2FBF9] rounded-xl border border-[#E3E8E5]">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-[#0B1412]">AI Mode</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!isAIMode}
                    onChange={(e) => setIsAIMode(!e.target.checked)}
                    className="sr-only peer"
                    disabled={isSaving || isLoading || isGeneratingAI}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
                <span className="text-sm font-medium text-[#0B1412]">Manual Mode</span>
              </div>
            </div>

            {/* AI Mode Content */}
            {isAIMode && (
              <div className="input-group">
                <label className="input-label">
                  Describe the day you want to create
                </label>
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="e.g., Create a day for exploring historic temples in the morning, followed by a traditional lunch, and ending with a visit to cultural landmarks..."
                  className="input-field resize-none"
                  rows={4}
                  disabled={isSaving || isLoading || isGeneratingAI}
                />
              </div>
            )}

            {/* Manual Mode Content */}
            {!isAIMode && (
              <>
                {/* Date Input */}
                <div className="input-group">
                  <label className="input-label">
                    Date *
                  </label>
                  <input
                    type="date"
                    value={dayDate}
                    onChange={(e) => setDayDate(e.target.value)}
                    className="input-field"
                    required
                    disabled={isSaving || isLoading || isGeneratingAI}
                  />
                </div>

                {/* Day Title */}
                <div className="input-group">
                  <label className="input-label">
                    Day Title *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Short title for this day (max 25 characters)"
                    className="input-field"
                    maxLength={25}
                    required
                    disabled={isSaving || isLoading || isGeneratingAI}
                  />
                  <div className="text-xs text-[#6F7974] mt-1">
                    {title.length}/25 characters
                  </div>
                </div>

                {/* Day Summary */}
                <div className="input-group">
                  <label className="input-label">
                    Day Summary *
                  </label>
                  <textarea
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    placeholder="Describe what this day will include..."
                    className="input-field resize-none"
                    rows={3}
                    required
                    disabled={isSaving || isLoading || isGeneratingAI}
                  />
                </div>

                {/* Accommodation */}
                <div className="input-group">
                  <label className="input-label">
                    Accommodation
                  </label>
                  <input
                    type="text"
                    value={lodgingInfo}
                    onChange={(e) => setLodgingInfo(e.target.value)}
                    placeholder="Hotel name, address, or accommodation details (optional)"
                    className="input-field"
                    disabled={isSaving || isLoading || isGeneratingAI}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-[#E3E8E5] flex items-center justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={isSaving || isLoading || isGeneratingAI}
            className="btn-secondary"
          >
            Cancel
          </button>
          {isAIMode ? (
            <button
              onClick={handleAIApply}
              disabled={
                isSaving || 
                isLoading || 
                isGeneratingAI ||
                !aiPrompt.trim()
              }
              className="btn-primary"
            >
              {isGeneratingAI ? (
                <>
                  <div className="loading-spinner w-4 h-4 mr-2"></div>
                  Applying Changes...
                </>
              ) : (
                'Apply Changes'
              )}
            </button>
          ) : (
            <div className="relative">
              <button
                data-button="day-save-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen(!isDropdownOpen);
                }}
                disabled={isLoading || isSaving || isGeneratingAI || !dayDate || !title || !summary}
                className="btn-primary flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <div className="loading-spinner w-4 h-4 mr-2"></div>
                    Generating...
                  </>
                ) : isSaving ? (
                  <>
                    <div className="loading-spinner w-4 h-4 mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <span className="mr-2">💾</span>
                    Save Day
                    <span className="ml-2">▲</span>
                  </>
                )}
              </button>
              
              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div 
                  data-dropdown="day-save-dropdown"
                  className="absolute bottom-full right-0 mb-2 bg-white border border-[#E3E8E5] rounded-xl shadow-lg overflow-hidden z-50"
                  onClick={(e) => e.stopPropagation()}
                  style={{ 
                    minWidth: '280px'
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDropdownAction('saveWithActivities');
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-[#F2FBF9] flex items-center font-medium text-[#0B1412] transition-colors duration-200"
                  >
                    <span className="mr-3">🤖</span>
                    Save & Generate Activities
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDropdownAction('saveAndRecommendations');
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-[#F2FBF9] flex items-center font-medium text-[#0B1412] border-t border-[#E3E8E5] transition-colors duration-200"
                  >
                    <span className="mr-3">💡</span>
                    Save & Show Recommendations
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDropdownAction('save');
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-[#F2FBF9] flex items-center font-medium text-[#0B1412] border-t border-[#E3E8E5] transition-colors duration-200"
                  >
                    <span className="mr-3">📝</span>
                    Save Day Only
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddDayModal;
